import {
  FavoriteEntity,
  EntityRefLink,
  useEntity,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { CatalogIcon } from '@backstage/core-components';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
} from '../../../theme/tokens';
import { renderLifecycleBadge } from '../../entity-explorer/shared';
import { EntityHeaderContextMenu } from './EntityHeaderContextMenu';
import { EntityHeaderMeta } from './EntityHeaderMeta';
import { useEntityPageModel } from './useEntityPageModel';

const MAX_VISIBLE_TAGS = 6;

const EntityTagList = ({ tags }: { tags: string[] }) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const extraCount = tags.length - visibleTags.length;

  if (!tags.length) {
    return null;
  }

  return (
    <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
      {visibleTags.map(tag => (
        <Box
          key={tag}
          component="span"
          sx={{
            alignItems: 'center',
            backgroundColor: getSurfaceColor(theme, 'subtle'),
            border: `1px solid ${getSurfaceBorder(theme)}`,
            borderRadius: 999,
            color: tokens.textSecondary,
            display: 'inline-flex',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            px: 1.25,
            py: 0.7,
          }}
        >
          {tag}
        </Box>
      ))}
      {extraCount > 0 ? (
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
            borderRadius: 999,
            color: theme.palette.primary.main,
            display: 'inline-flex',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1,
            px: 1.25,
            py: 0.7,
          }}
        >
          +{extraCount}
        </Box>
      ) : null}
    </Stack>
  );
};

export const EntityHero = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { entity } = useEntity();
  const model = useEntityPageModel();
  const presentation = useEntityPresentation(entity);
  const EntityIcon = presentation.Icon || CatalogIcon;

  return (
    <Box
      sx={{
        pb: { sm: 1.5, xs: 1.25 },
        pt: { sm: 0.5, xs: 0.25 },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          mb: { sm: 2, xs: 1.5 },
        }}
      >
        <Breadcrumbs
          aria-label="Entity breadcrumbs"
          separator="/"
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiBreadcrumbs-ol': { alignItems: 'center' },
            '& .MuiBreadcrumbs-separator': {
              color: tokens.textMuted,
              mx: 0.75,
            },
          }}
        >
          {model.hero.breadcrumbs.map(crumb =>
            crumb.to ? (
              <Link
                color="inherit"
                component={RouterLink}
                key={crumb.label}
                sx={{
                  color: tokens.textSecondary,
                  fontSize: 12,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': {
                    color: tokens.textPrimary,
                  },
                }}
                to={crumb.to}
                underline="none"
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography
                key={crumb.label}
                sx={{
                  color: tokens.textSecondary,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {crumb.label}
              </Typography>
            ),
          )}
        </Breadcrumbs>
        <EntityHeaderContextMenu />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: { md: 3, xs: 2.25 },
          gridTemplateColumns: {
            lg: 'minmax(0, 1fr) auto',
            xs: 'minmax(0, 1fr)',
          },
        }}
      >
        <Stack spacing={2}>
          <Stack
            alignItems="flex-start"
            direction={{ sm: 'row', xs: 'column' }}
            spacing={2}
          >
            <Box
              aria-hidden
              sx={{
                alignItems: 'center',
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                borderRadius: '12px',
                color: theme.palette.primary.main,
                display: 'inline-flex',
                flexShrink: 0,
                height: 44,
                justifyContent: 'center',
                width: 44,
              }}
            >
              <EntityIcon fontSize="small" />
            </Box>

            <Stack spacing={1.25} sx={{ minWidth: 0 }}>
              <Stack
                alignItems="center"
                direction="row"
                spacing={0.75}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  component="h1"
                  sx={{
                    color: tokens.textPrimary,
                    fontSize: { md: 34, sm: 30, xs: 26 },
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.05,
                    minWidth: 0,
                  }}
                >
                  {model.hero.title}
                </Typography>
                <FavoriteEntity
                  entity={entity}
                  size="small"
                  style={{
                    color: tokens.textMuted,
                    flexShrink: 0,
                    marginLeft: 2,
                    padding: 2,
                  }}
                />
              </Stack>

              {model.hero.description ? (
                <Typography
                  sx={{
                    color: tokens.textSecondary,
                    fontSize: 15,
                    lineHeight: 1.65,
                    maxWidth: 760,
                  }}
                >
                  {model.hero.description}
                </Typography>
              ) : null}

              <EntityTagList tags={model.hero.tags} />
            </Stack>
          </Stack>
        </Stack>

        <Box
          component="dl"
          sx={{
            alignContent: 'start',
            columnGap: { sm: 2.5, xs: 1.75 },
            display: 'grid',
            gridTemplateColumns: {
              sm: 'repeat(2, minmax(110px, max-content))',
              xs: 'repeat(2, minmax(0, 1fr))',
            },
            justifyContent: { lg: 'end', xs: 'start' },
            margin: 0,
            rowGap: 1.5,
          }}
        >
          <EntityHeaderMeta label="Owner">
            {model.hero.owner.reference ? (
              <EntityRefLink
                color="inherit"
                entityRef={model.hero.owner.reference.entityRef}
                title={model.hero.owner.reference.title}
              />
            ) : (
              <Typography
                sx={{ color: tokens.textMuted, fontSize: 14, fontWeight: 600 }}
              >
                —
              </Typography>
            )}
          </EntityHeaderMeta>

          <EntityHeaderMeta label="Lifecycle">
            {model.hero.lifecycle.value ? (
              renderLifecycleBadge(model.hero.lifecycle.value)
            ) : (
              <Typography
                sx={{ color: tokens.textMuted, fontSize: 14, fontWeight: 600 }}
              >
                —
              </Typography>
            )}
          </EntityHeaderMeta>
        </Box>
      </Box>
    </Box>
  );
};
